import type { CurrencyAgg, FiatAgg, ReportBasis } from '@apx/admin-sdk';
import { fmtMoney } from '../format.js';

export type Unit = 'credits' | 'fiat';

/** Display window presets shared by the report filter bar. */
export type Since = '1h' | '24h' | '7d' | '30d';

export const SINCE_MS: Record<Since, number> = {
  '1h': 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

export const SINCE_LABEL: Record<Since, string> = {
  '1h': 'last hour',
  '24h': 'last 24h',
  '7d': 'last 7 days',
  '30d': 'last 30 days',
};

/**
 * Report timezone filter — apx stores `Bet.placedAt`/`settledAt` as UTC, but
 * downstream consumers (e.g. shortie, GMT+8) close their "day" in local time.
 * Choosing a tz here makes the range label, timeseries buckets, and per-row
 * timestamps all read in that tz instead of the browser's local zone.
 */
export const REPORT_TZ_STORAGE_KEY = 'report.tz';

/**
 * Report basis filter — which clock the window is measured against. Sticky
 * across reloads for the same reason as the tz: an operator reconciling
 * against one integrator wants the same basis every time they open a report,
 * and silently reverting to the default would change their numbers between
 * sessions with no visible cause.
 */
export const REPORT_BASIS_STORAGE_KEY = 'report.basis';

export const BASIS_LABEL: Record<ReportBasis, string> = {
  settled: 'Settle time',
  placed: 'Bet time',
};

/**
 * The one-line explanation shown under the filter bar. Which clock produced a
 * turnover figure is not inferable from the figure, so the active basis is
 * always stated on screen rather than left to the operator's memory.
 */
export const BASIS_HINT: Record<ReportBasis, string> = {
  settled:
    'Counted on when each round settled — matches how an integrator dates the same transaction.',
  placed: 'Counted on when each bet was accepted — apx’s historical basis.',
};

/** Narrows a stored/URL string to a basis, falling back to the default. */
export function parseBasis(raw: string | null | undefined): ReportBasis {
  return raw === 'placed' || raw === 'settled' ? raw : 'settled';
}

/** Browser's resolved IANA tz name, falling back to UTC if `Intl` can't resolve one. */
export function resolveBrowserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Timezone `<select>` options — browser-local first, then common presets. De-duped by value (e.g. a browser already on Asia/Singapore doesn't get a repeated entry). */
export function tzSelectOptions(browserTz: string): Array<{ value: string; label: string }> {
  const presets = [
    { value: browserTz, label: `Browser (${browserTz})` },
    { value: 'Asia/Singapore', label: 'Singapore (GMT+8)' },
    { value: 'Asia/Jakarta', label: 'Jakarta (GMT+7)' },
    { value: 'UTC', label: 'UTC' },
  ];
  return presets.filter((opt, idx) => presets.findIndex((o) => o.value === opt.value) === idx);
}

/**
 * Converts a `datetime-local` wall-clock string ("YYYY-MM-DDTHH:mm:ss") — as
 * if that clock reading happened in `tz` — into the UTC instant it refers to.
 * DST-safe: resolves via two passes through `Intl` rather than a fixed UTC
 * offset, so it's correct across a DST transition (ambiguous/skipped local
 * times resolve to whichever offset `Intl` reports for the first guess).
 */
export function zonedWallClockToUTC(local: string, tz: string): Date {
  const [d, t] = local.split('T');
  const [y, mo, da] = d!.split('-').map(Number);
  const [h, mi, s = 0] = (t ?? '00:00:00').split(':').map(Number);
  const guess = Date.UTC(y!, mo! - 1, da!, h!, mi!, s);
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date(guess)).reduce<Record<string, string>>((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  const asTz = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );
  return new Date(guess - (asTz - guess));
}

/**
 * Dominant-currency fiat row (largest by turnover) — handles the rare
 * multi-currency tenant by surfacing the biggest; null when no wallet rows.
 * Sorted via BigInt comparison (not `Number(...)`) so the ranking stays
 * correct for turnover figures beyond `Number.MAX_SAFE_INTEGER`.
 */
export function pickFiat(fiat: FiatAgg[] | undefined): FiatAgg | null {
  if (!fiat || fiat.length === 0) return null;
  return [...fiat].sort((a, b) => {
    const diff = BigInt(b.totalBet) - BigInt(a.totalBet);
    return diff > 0n ? 1 : diff < 0n ? -1 : 0;
  })[0]!;
}

/** An amount (integer minor units) in its currency, followed by the code:
 *  "1,234.56 USD", "169,000 IDR". BigInt-exact, never compact. Without a
 *  currency it is the plain grouped integer — see `fmtMoney`. */
export function money(s: string, ccy?: string): string {
  const body = fmtMoney(s, ccy);
  return ccy ? `${body} ${ccy}` : body;
}

export function pct(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

/**
 * BigInt-safe average — divides a credit/fiat-unit total string by a count
 * without losing precision on the (potentially huge) numerator. Unlike
 * `Math.round(Number(total) / count)`, which converts `total` to a float
 * before dividing, this divides in BigInt space first (exact) and only
 * converts the already-small quotient to `Number` for display.
 */
export function avgCredits(total: string, count: number): number {
  if (count <= 0) return 0;
  try {
    return Number(BigInt(total) / BigInt(count));
  } catch {
    return Math.round(Number(total) / count);
  }
}

/**
 * Small per-currency breakdown table — rendered alongside the existing
 * blended KPI/summary view whenever a report's `byCurrency` companion has
 * more than one entry. At one currency (prod today) the blended total
 * already says it all, so callers should skip this.
 */
export function renderCurrencyBreakdown(byCurrency: CurrencyAgg[]): string {
  if (byCurrency.length <= 1) return '';
  return `<div class="data-table-wrap" style="margin-top:8px"><table class="data-table">
    <thead><tr>
      <th>Currency</th><th class="num">Bets</th><th class="num">Turnover</th>
      <th class="num">Payout</th><th class="num">House take</th><th class="num">RTP</th>
    </tr></thead>
    <tbody>${byCurrency
      .map(
        (c) => `<tr>
          <td><code>${escapeHtml(c.currency)}</code></td>
          <td class="num">${c.betCount.toLocaleString()}</td>
          <td class="num">${money(c.totalBet, c.currency)}</td>
          <td class="num">${money(c.totalPayout, c.currency)}</td>
          <td class="num ${Number(c.houseTake) >= 0 ? 'pos' : 'neg'}">${money(c.houseTake, c.currency)}</td>
          <td class="num">${pct(c.rtp)}</td>
        </tr>`,
      )
      .join('')}</tbody>
  </table></div>`;
}

/** A signed percentage delta vs a prior value, rendered as a coloured chip. */
export function deltaChip(
  current: number,
  prior: number,
  opts: { goodWhenUp?: boolean; neutral?: boolean } = {},
): string {
  if (!Number.isFinite(prior) || prior === 0) {
    return `<span class="stat-delta stat-delta-flat" title="No comparable prior window">— new</span>`;
  }
  const change = (current - prior) / Math.abs(prior);
  const up = change >= 0;
  const arrow = up ? '▲' : '▼';
  const magnitude = `${Math.abs(change * 100).toFixed(1)}%`;
  if (opts.neutral) {
    return `<span class="stat-delta stat-delta-flat">${arrow} ${magnitude}</span>`;
  }
  const good = opts.goodWhenUp === false ? !up : up;
  const cls = good ? 'stat-delta-up' : 'stat-delta-down';
  return `<span class="stat-delta ${cls}">${arrow} ${magnitude} <span class="stat-delta-vs">vs prev</span></span>`;
}

/**
 * Transaction-id cell.
 *
 * The value is `Bet.requestId` — the same string apx sent the integrator wallet
 * as `tx_id` and therefore the one their transaction list prints. Rendered in
 * full rather than truncated: an operator's whole job here is matching it
 * against another screen, and a shortened id cannot be compared or pasted.
 * Click copies it, since the next step is always pasting it somewhere.
 */
export function txIdCell(transactionId: string): string {
  const safe = escapeHtml(transactionId);
  return `<td><code class="tx-id" data-copy-tx="${safe}" role="button" tabindex="0" title="Copy transaction id">${safe}</code></td>`;
}

/** What each `Bet.acceptStatus` means, as the cell's tooltip. */
const ACCEPT_STATUS_HINT: Record<string, string> = {
  confirmed: 'Debit landed — the stake is with the house.',
  pending: 'Bet accepted, debit still in flight at the integrator wallet.',
  rejected: 'The integrator declined or failed the debit — no stake was captured.',
  cancelled: 'Player undid the bet in the betting window; the stake was rolled back.',
};

/**
 * Accept-status cell, rendered immediately after a row's outcome.
 *
 * `outcome` and `acceptStatus` are orthogonal columns and neither alone
 * describes a bet: `PENDING` is a live in-window bet when `confirmed` and a
 * debit the wallet declined when `rejected`, while `VOID` is a player undo when
 * `cancelled` and an admin refund when `confirmed`. An operator reading one
 * column was guessing between those, which is why the pair now travels
 * together everywhere a bet row is shown.
 *
 * `confirmed` is the overwhelmingly common value and the unremarkable one, so
 * it renders muted; everything else is a bet whose money did not move the way
 * the outcome column implies, and is called out.
 */
export function acceptStatusCell(acceptStatus: string | undefined): string {
  if (!acceptStatus) return '<td><span class="muted">—</span></td>';
  const cls = acceptStatus === 'confirmed' ? '' : acceptStatus === 'pending' ? '' : 'warn';
  const style = acceptStatus === 'confirmed' ? ' style="opacity:.6"' : '';
  const hint = ACCEPT_STATUS_HINT[acceptStatus] ?? 'Debit-acceptance lifecycle.';
  return `<td><span class="pill ${cls}"${style} title="${escapeHtml(hint)}">${escapeHtml(acceptStatus)}</span></td>`;
}

/**
 * Installs one delegated copy handler for every `txIdCell` under `root`.
 * Idempotent per element — calling it again on a re-rendered container is safe
 * because the listener lives on the container, not the cells it replaces.
 */
export function wireTxIdCopy(root: HTMLElement, onCopied: (id: string) => void): void {
  if (root.dataset.txCopyWired === '1') return;
  root.dataset.txCopyWired = '1';
  const copy = (el: HTMLElement): void => {
    const id = el.dataset.copyTx;
    if (!id) return;
    void navigator.clipboard?.writeText(id).then(
      () => onCopied(id),
      () => {
        /* clipboard blocked (insecure context / denied) — the id is still on screen */
      },
    );
  };
  root.addEventListener('click', (ev) => {
    const el = (ev.target as HTMLElement).closest<HTMLElement>('[data-copy-tx]');
    if (el) copy(el);
  });
  root.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const el = (ev.target as HTMLElement).closest<HTMLElement>('[data-copy-tx]');
    if (!el) return;
    ev.preventDefault();
    copy(el);
  });
}

export function escapeHtml(s: string | null | undefined): string {
  return (s ?? '').replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
